// src/components/admin/settings/OrganismePaiementSettings.jsx
import React, { useState, useEffect } from 'react';
import { CreditCard, Plus, Edit, Trash2, Check, X, AlertCircle } from 'lucide-react';
import { organismePaiementApi } from '../../../api/organisme-paiement-api';
import { toast } from 'react-toastify';

const OrganismePaiementSettings = () => {
  const [organismes, setOrganismes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentOrganisme, setCurrentOrganisme] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formData, setFormData] = useState({
    nomOrganisme: '',
    adresse: '',
    nomContact: '',
    prenomContact: '',
    telContact: '',
    defaultOrganisme: false
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Fetch real data from API
    const fetchData = async () => {
      try {
        setLoading(true);
        const organismesData = await organismePaiementApi.getOrganismesPaiement();
        setOrganismes(organismesData);
      } catch (error) {
        console.error('Error fetching payment organizations:', error);
        toast.error('Erreur lors du chargement des organismes de paiement');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when field is filled
    if (value && errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleEdit = (organisme) => {
    setCurrentOrganisme(organisme);
    setFormData({
      nomOrganisme: organisme.nomOrganisme,
      adresse: organisme.adresse,
      nomContact: organisme.nomContact,
      prenomContact: organisme.prenomContact,
      telContact: organisme.telContact,
      defaultOrganisme: organisme.defaultOrganisme
    });
    setErrors({});
    setIsEditing(true);
  };

  const handleCreate = () => {
    setCurrentOrganisme(null);
    setFormData({
      nomOrganisme: '',
      adresse: '',
      nomContact: '',
      prenomContact: '',
      telContact: '',
      defaultOrganisme: false
    });
    setErrors({});
    setIsEditing(true);
  };

  const handleDelete = (organisme) => {
    setCurrentOrganisme(organisme);
    setIsDeleting(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      
      // Delete organisme via API
      await organismePaiementApi.deleteOrganismePaiement(currentOrganisme.id);
      
      // Update local state
      const updatedOrganismes = organismes.filter(o => o.id !== currentOrganisme.id);
      
      // If default organisme is deleted, set the first remaining one as default
      if (currentOrganisme.defaultOrganisme && updatedOrganismes.length > 0) {
        await organismePaiementApi.setDefaultOrganisme(updatedOrganismes[0].id);
        updatedOrganismes[0].defaultOrganisme = true;
      }
      
      setOrganismes(updatedOrganismes);
      setIsDeleting(false);
      
      toast.success('Organisme de paiement supprimé avec succès');
    } catch (error) {
      console.error('Error deleting payment organization:', error);
      toast.error('Erreur lors de la suppression de l\'organisme de paiement');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nomOrganisme) {
      newErrors.nomOrganisme = 'Le nom de l\'organisme est requis';
    }
    
    if (!formData.adresse) {
      newErrors.adresse = 'L\'adresse est requise';
    }
    
    if (!formData.nomContact) {
      newErrors.nomContact = 'Le nom du contact est requis';
    }
    
    if (!formData.prenomContact) {
      newErrors.prenomContact = 'Le prénom du contact est requis';
    }
    
    if (!formData.telContact) {
      newErrors.telContact = 'Le téléphone est requis';
    } else if (!/^(\+212|0)[5-7][0-9]{8}$/.test(formData.telContact)) {
      newErrors.telContact = 'Format de téléphone invalide';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      const organismeData = {
        nomOrganisme: formData.nomOrganisme,
        adresse: formData.adresse,
        nomContact: formData.nomContact,
        prenomContact: formData.prenomContact,
        telContact: formData.telContact,
        defaultOrganisme: formData.defaultOrganisme,
        piecesJointes: null // Handle file upload separately if needed
      };
      
      let savedOrganisme;
      
      if (currentOrganisme) {
        // Update existing organisme
        organismeData.id = currentOrganisme.id;
        savedOrganisme = await organismePaiementApi.updateOrganismePaiement(organismeData);
        toast.success('Organisme de paiement mis à jour avec succès');
        
        // Update existing organisme in local state
        const updatedOrganismes = organismes.map(o => {
          if (o.id === currentOrganisme.id) {
            return savedOrganisme;
          }
          
          // If setting this one as default, remove default from others
          if (formData.defaultOrganisme && o.defaultOrganisme && o.id !== currentOrganisme.id) {
            return { ...o, defaultOrganisme: false };
          }
          
          return o;
        });
        
        setOrganismes(updatedOrganismes);
      } else {
        // Create new organisme
        savedOrganisme = await organismePaiementApi.createOrganismePaiement(organismeData);
        toast.success('Organisme de paiement créé avec succès');
        
        // If setting this one as default, update all others
        let updatedOrganismes = [...organismes];
        if (formData.defaultOrganisme) {
          updatedOrganismes = updatedOrganismes.map(o => ({
            ...o,
            defaultOrganisme: false
          }));
        }
        
        setOrganismes([...updatedOrganismes, savedOrganisme]);
      }
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving payment organization:', error);
      toast.error('Erreur lors de la sauvegarde de l\'organisme de paiement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <button 
          onClick={handleCreate}
          className="flex items-center px-3 py-1.5 bg-green-700 text-white text-sm rounded-md hover:bg-green-800"
        >
          <Plus size={16} className="mr-1" />
          Ajouter un organisme
        </button>
      </div>

      {loading ? (
        <div className="py-10 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-green-700 border-r-transparent"></div>
          <p className="mt-2 text-gray-500">Chargement des organismes de paiement...</p>
        </div>
      ) : organismes.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-lg border border-gray-200">
          <CreditCard size={40} className="mx-auto text-gray-400 mb-2" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun organisme de paiement</h3>
          <p className="mt-1 text-sm text-gray-500">
            Commencez par ajouter un organisme de paiement pour les transactions.
          </p>
          <div className="mt-6">
            <button
              onClick={handleCreate}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-700 hover:bg-green-800"
            >
              <Plus size={16} className="mr-1" />
              Ajouter un organisme
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {organismes.map((organisme) => (
              <li key={organisme.id}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <p className="text-sm font-medium text-green-700 truncate">
                        {organisme.nomOrganisme}
                      </p>
                      {organisme.defaultOrganisme && (
                        <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Par défaut
                        </span>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(organisme)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Modifier"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(organisme)}
                        className="text-red-600 hover:text-red-900"
                        title="Supprimer"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 sm:flex sm:justify-between">
                    <div className="sm:flex">
                      <p className="flex items-center text-sm text-gray-500">
                        {organisme.adresse}
                      </p>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                      <p>
                        {organisme.prenomContact} {organisme.nomContact} • {organisme.telContact}
                      </p>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Edit/Create Modal */}
      {isEditing && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="fixed inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>

            <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                      <CreditCard className="h-6 w-6 text-yellow-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg font-medium leading-6 text-gray-900">
                        {currentOrganisme ? 'Modifier l\'organisme' : 'Nouvel organisme de paiement'}
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Remplissez les informations de l'organisme de paiement ci-dessous.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 space-y-4">
                    <div>
                      <label htmlFor="nomOrganisme" className="block text-sm font-medium text-gray-700">
                        Nom de l'organisme *
                      </label>
                      <input
                        type="text"
                        id="nomOrganisme"
                        name="nomOrganisme"
                        value={formData.nomOrganisme}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full rounded-md border ${
                          errors.nomOrganisme ? 'border-red-300' : 'border-gray-300'
                        } shadow-sm py-2 px-3 focus:border-green-500 focus:outline-none focus:ring-green-500 sm:text-sm`}
                      />
                      {errors.nomOrganisme && (
                        <p className="mt-1 text-sm text-red-600">{errors.nomOrganisme}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="adresse" className="block text-sm font-medium text-gray-700">
                        Adresse *
                      </label>
                      <textarea
                        id="adresse"
                        name="adresse"
                        rows="2"
                        value={formData.adresse}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full rounded-md border ${
                          errors.adresse ? 'border-red-300' : 'border-gray-300'
                        } shadow-sm py-2 px-3 focus:border-green-500 focus:outline-none focus:ring-green-500 sm:text-sm`}
                      ></textarea>
                      {errors.adresse && (
                        <p className="mt-1 text-sm text-red-600">{errors.adresse}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="nomContact" className="block text-sm font-medium text-gray-700">
                          Nom du contact *
                        </label>
                        <input
                          type="text"
                          id="nomContact"
                          name="nomContact"
                          value={formData.nomContact}
                          onChange={handleInputChange}
                          className={`mt-1 block w-full rounded-md border ${
                            errors.nomContact ? 'border-red-300' : 'border-gray-300'
                          } shadow-sm py-2 px-3 focus:border-green-500 focus:outline-none focus:ring-green-500 sm:text-sm`}
                        />
                        {errors.nomContact && (
                          <p className="mt-1 text-sm text-red-600">{errors.nomContact}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="prenomContact" className="block text-sm font-medium text-gray-700">
                          Prénom du contact *
                        </label>
                        <input
                          type="text"
                          id="prenomContact"
                          name="prenomContact"
                          value={formData.prenomContact}
                          onChange={handleInputChange}
                          className={`mt-1 block w-full rounded-md border ${
                            errors.prenomContact ? 'border-red-300' : 'border-gray-300'
                          } shadow-sm py-2 px-3 focus:border-green-500 focus:outline-none focus:ring-green-500 sm:text-sm`}
                        />
                        {errors.prenomContact && (
                          <p className="mt-1 text-sm text-red-600">{errors.prenomContact}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="telContact" className="block text-sm font-medium text-gray-700">
                        Téléphone du contact *
                      </label>
                      <input
                        type="tel"
                        id="telContact"
                        name="telContact"
                        value={formData.telContact}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full rounded-md border ${
                          errors.telContact ? 'border-red-300' : 'border-gray-300'
                        } shadow-sm py-2 px-3 focus:border-green-500 focus:outline-none focus:ring-green-500 sm:text-sm`}
                        placeholder="+212 522 123 456"
                      />
                      {errors.telContact && (
                        <p className="mt-1 text-sm text-red-600">{errors.telContact}</p>
                      )}
                    </div>

                    <div className="relative flex items-start">
                      <div className="flex h-5 items-center">
                        <input
                          id="defaultOrganisme"
                          name="defaultOrganisme"
                          type="checkbox"
                          checked={formData.defaultOrganisme}
                          onChange={handleInputChange}
                          className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="defaultOrganisme" className="font-medium text-gray-700">
                          Organisme par défaut
                        </label>
                        <p className="text-gray-500">
                          Définir comme organisme de paiement par défaut pour toutes les transactions.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex w-full justify-center rounded-md border border-transparent bg-green-700 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        {currentOrganisme ? 'Mise à jour...' : 'Création...'}
                      </>
                    ) : (
                      currentOrganisme ? 'Enregistrer' : 'Créer'
                    )}
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                    onClick={() => setIsEditing(false)}
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleting && currentOrganisme && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="fixed inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>

            <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg font-medium leading-6 text-gray-900">
                      Supprimer l'organisme
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Êtes-vous sûr de vouloir supprimer l'organisme de paiement <strong>{currentOrganisme.nomOrganisme}</strong> ? Cette action ne peut pas être annulée.
                      </p>
                      
                      {currentOrganisme.defaultOrganisme && (
                        <div className="mt-3 rounded-md bg-yellow-50 p-4">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <AlertCircle className="h-5 w-5 text-yellow-400" />
                            </div>
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-yellow-800">Attention</h3>
                              <div className="mt-2 text-sm text-yellow-700">
                                <p>
                                  Cet organisme est défini comme organisme de paiement par défaut. En le supprimant, vous devrez définir un nouvel organisme par défaut.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  disabled={loading}
                  className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={confirmDelete}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      Suppression...
                    </>
                  ) : (
                    'Supprimer'
                  )}
                </button>
                <button
                  type="button"
                  className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                  onClick={() => setIsDeleting(false)}
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganismePaiementSettings;